import { useState, useEffect } from 'react';
import { addDoc, collection } from 'firebase/firestore';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { saveNeedsFromNotes } from '../../utils/needsService';
import { useAuth } from '../../contexts/AuthContext';
import { AdnReport } from '../../types/adnReport';
import { FormInput } from '../ui/FormInput';
import { MeetingTypeSelect } from '../ui/MeetingTypeSelect';
import { WorshipReportSelect } from '../ui/WorshipReportSelect';
import { WorshipReport } from '../../types/worshipReport';
import { toast } from 'sonner';

interface AdnFormProps {
  onSubmit?: (data: AdnReport) => void;
  onCancel?: () => void;
}

export default function AdnForm({ onSubmit }: AdnFormProps) {
  const { user } = useAuth();
  const [formData, setFormData] = useState<Partial<AdnReport>>({
    date: '',
    worshipReportId: '',
    meetingTypeId: '',
    newVisitors: { men: 0, women: 0 },
    visitorDecisions: {
      undecided: 0,
      wantsToJoin: { men: 0, women: 0 },
      wantsToGiveLifeToJesus: { men: 0, women: 0 }
    },
    notes: '',
    needsNotes: ''
  });

  // Auto-populate date and meeting type from selected worship report
  useEffect(() => {
    const fetchWorshipReportData = async () => {
      if (!formData.worshipReportId) {
        setFormData(prev => ({ ...prev, date: '', meetingTypeId: '' }));
        return;
      }

      try {
        const worshipReportRef = doc(db, 'worshipReports', formData.worshipReportId);
        const worshipReportSnap = await getDoc(worshipReportRef);
        
        if (worshipReportSnap.exists()) {
          const worshipReportData = worshipReportSnap.data() as WorshipReport;
          setFormData(prev => ({
            ...prev,
            date: worshipReportData.date,
            meetingTypeId: worshipReportData.meetingTypeId
          }));
        }
      } catch (error) {
        console.error('Error fetching worship report:', error);
      }
    };

    fetchWorshipReportData();
  }, [formData.worshipReportId]);

  const calculateTotals = () => {
    const totalNewVisitors = (formData.newVisitors?.men || 0) + (formData.newVisitors?.women || 0);
    const totalWantsToJoin = (formData.visitorDecisions?.wantsToJoin?.men || 0) + (formData.visitorDecisions?.wantsToJoin?.women || 0);
    const totalWantsToGiveLifeToJesus = (formData.visitorDecisions?.wantsToGiveLifeToJesus?.men || 0) + (formData.visitorDecisions?.wantsToGiveLifeToJesus?.women || 0);
    return { totalNewVisitors, totalWantsToJoin, totalWantsToGiveLifeToJesus };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (!formData.meetingTypeId || !formData.newVisitors || !formData.visitorDecisions) {
        throw new Error('Missing required fields');
      }
      
      if (!formData.worshipReportId) {
        throw new Error('Veuillez sélectionner le rapport de culte à annexer');
      }

      const { totalNewVisitors, totalWantsToJoin } = calculateTotals();
      
      const reportData = {
        ...formData,
        totalNewVisitors,
        totalWantsToJoin,
        totalWantsToGiveLifeToJesus: calculateTotals().totalWantsToGiveLifeToJesus,
        createdAt: new Date(),
        createdBy: user?.id
      } as AdnReport;

      const docRef = await addDoc(collection(db, 'adnReports'), reportData);
      
      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'adn',
          department: 'ADN - Amis des Nouveaux',
          needsNotes: formData.needsNotes,
          reportDate: formData.date!
        });
      }
      
      // Sauvegarder les besoins si présents
      if (formData.needsNotes) {
        await saveNeedsFromNotes({
          reportId: docRef.id,
          reportType: 'adn',
          department: 'ADN - Amis des Nouveaux',
          needsNotes: formData.needsNotes,
          reportDate: formData.date!
        });
      }
      
      if (onSubmit) {
        onSubmit({ ...reportData, id: docRef.id });
      }

      // Reset form
      setFormData({
        date: new Date().toISOString().split('T')[0],
        worshipReportId: '',
        meetingTypeId: '',
        newVisitors: { men: 0, women: 0 },
        visitorDecisions: {
          undecided: 0,
          wantsToJoin: { men: 0, women: 0 },
          wantsToGiveLifeToJesus: { men: 0, women: 0 }
        },
        notes: '',
        needsNotes: ''
      });

      toast.success('Rapport ADN enregistré avec succès');
    } catch (error) {
      console.error('Error saving ADN report:', error);
      toast.error('Erreur lors de l\'enregistrement du rapport ADN');
    }
  };

  const handleNewVisitorsChange = (field: 'men' | 'women', value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      newVisitors: {
        ...prev.newVisitors!,
        [field]: numValue
      }
    }));
  };

  const handleLifeToJesusChange = (field: 'men' | 'women', value: string) => {
    const numValue = parseInt(value) || 0;
    setFormData(prev => ({
      ...prev,
      visitorDecisions: {
        ...prev.visitorDecisions!,
        wantsToGiveLifeToJesus: {
          ...prev.visitorDecisions!.wantsToGiveLifeToJesus,
          [field]: numValue
        }
      }
    }));
  };

  const handleDecisionChange = (type: 'undecided' | 'men' | 'women', value: string) => {
    const numValue = parseInt(value) || 0;
    
    if (type === 'undecided') {
      setFormData(prev => ({
        ...prev,
        visitorDecisions: {
          ...prev.visitorDecisions!,
          undecided: numValue
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        visitorDecisions: {
          ...prev.visitorDecisions!,
          wantsToJoin: {
            ...prev.visitorDecisions!.wantsToJoin,
            [type]: numValue
          }
        }
      }));
    }
  };

  const { totalNewVisitors, totalWantsToJoin, totalWantsToGiveLifeToJesus } = calculateTotals();

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Informations générales */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Informations générales</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <WorshipReportSelect
              value={formData.worshipReportId || ''}
              onChange={(value) => setFormData(prev => ({ ...prev, worshipReportId: value }))}
              required
              departmentCollection="adnReports"
            />
          </div>
          <FormInput
            label="Date de la rencontre"
            type="date"
            value={formData.date || ''}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
           disabled={true}
          />
          <MeetingTypeSelect
            value={formData.meetingTypeId || ''}
            onChange={(value) => setFormData(prev => ({ ...prev, meetingTypeId: value }))}
            required={false}
           disabled={true}
          />
        </div>
      </div>

      {/* Nouveaux visiteurs */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Nouveaux visiteurs</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormInput
            type="number"
            min="0"
            label="Hommes"
            value={formData.newVisitors?.men || 0}
            onChange={(e) => handleNewVisitorsChange('men', e.target.value)}
          />
          <FormInput
            type="number"
            min="0"
            label="Femmes"
            value={formData.newVisitors?.women || 0}
            onChange={(e) => handleNewVisitorsChange('women', e.target.value)}
          />
        </div>
        <div className="mt-4 p-3 bg-[#00665C]/5 rounded-lg">
          <p className="text-[#004d45] font-medium">
            Total des nouveaux visiteurs: {totalNewVisitors}
          </p>
        </div>
      </div>

      {/* Décisions des visiteurs */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Décisions des nouveaux visiteurs</h2>
        <div className="space-y-4">
          <FormInput
            type="number"
            min="0"
            label="Personnes indécises"
            value={formData.visitorDecisions?.undecided || 0}
            onChange={(e) => handleDecisionChange('undecided', e.target.value)}
          />
          
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-700 mb-4">Veulent devenir membres</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                type="number"
                min="0"
                label="Hommes"
                value={formData.visitorDecisions?.wantsToJoin?.men || 0}
                onChange={(e) => handleDecisionChange('men', e.target.value)}
              />
              <FormInput
                type="number"
                min="0"
                label="Femmes"
                value={formData.visitorDecisions?.wantsToJoin?.women || 0}
                onChange={(e) => handleDecisionChange('women', e.target.value)}
              />
            </div>
          </div>
          
          <div className="border-t pt-4">
            <h3 className="font-medium text-gray-700 mb-4">Veulent donner leur vie à Jésus</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInput
                type="number"
                min="0"
                label="Hommes"
                value={formData.visitorDecisions?.wantsToGiveLifeToJesus?.men || 0}
                onChange={(e) => handleLifeToJesusChange('men', e.target.value)}
              />
              <FormInput
                type="number"
                min="0"
                label="Femmes"
                value={formData.visitorDecisions?.wantsToGiveLifeToJesus?.women || 0}
                onChange={(e) => handleLifeToJesusChange('women', e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="mt-4 space-y-2">
          <div className="p-3 bg-green-50 rounded-lg">
            <p className="text-green-800 font-medium">
              Total veulent devenir membres: {totalWantsToJoin}
            </p>
          </div>
          <div className="p-3 bg-[#00665C]/8 rounded-lg">
            <p className="text-[#004d45] font-medium">
              Total veulent donner leur vie à Jésus: {totalWantsToGiveLifeToJesus}
            </p>
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Notes / Observations
        </label>
        <textarea
          placeholder="Observations particulières..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.notes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
        />
      </div>

      {/* Besoins */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Liste des besoins
        </label>
        <textarea
          placeholder="Listez les besoins du département (un besoin par ligne)&#10;Exemple:&#10;- Cartes de visite pour nouveaux visiteurs&#10;- Goodies de bienvenue&#10;- Support d'information église"
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.needsNotes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
        </p>
      </div>

      {/* Besoins */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <label className="block text-sm font-medium text-gray-600 mb-2">
          Liste des besoins
        </label>
        <textarea
          placeholder="Listez les besoins du département (un besoin par ligne)..."
          className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-[#00665C] focus:border-[#00665C]"
          value={formData.needsNotes || ''}
          onChange={(e) => setFormData(prev => ({ ...prev, needsNotes: e.target.value }))}
        />
        <p className="mt-1 text-xs text-gray-500">
          Entrez un besoin par ligne. Ces besoins seront ajoutés à la liste de suivi des besoins du département.
        </p>
      </div>

      <div className="flex justify-end">
        <button
          type="submit"
          className="px-6 py-2 bg-[#00665C] text-white rounded-lg hover:bg-[#004d45] focus:outline-none focus:ring-2 focus:ring-[#00665C] focus:ring-offset-2"
        >
          Enregistrer le rapport ADN
        </button>
      </div>
    </form>
  );
}